import {Injectable, OnDestroy} from '@angular/core';
import {EventService} from './event.service';
import {filter, switchMap, take, takeWhile, map, tap, withLatestFrom} from 'rxjs/operators';
import {getAllMenuStateSeiten} from '../models/menuState';
import {ShellConfigService} from './shell-config.service';
import {UIWorkflowValidierung} from '../models/uiWorkflowValidierung';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {TranslationService} from '@lv1871/core';
import {interval, Subscription} from 'rxjs';
import {votumBlocksWorkflow} from '../models/uiArtefakt';

@Injectable({
  providedIn: 'root'
})
export class MenuWorkflowService implements OnDestroy {
  private _subscription: Subscription = Subscription.EMPTY;
  private _uiControlsRequestPending: boolean;
  private _uiControlsRequestPendingSubscription$ = Subscription.EMPTY;

  constructor(private _eventService: EventService, private _shellConfigService: ShellConfigService,
              public snackBar: MatSnackBar, private _translationService: TranslationService) {
    this._uiControlsRequestPendingSubscription$ = this._eventService.uiControlsRequestPending$.
    subscribe(requestPending => this._uiControlsRequestPending = requestPending);
    this.init();
  }

  public init() {
    this._subscription.unsubscribe();

    this._subscription = this._eventService.menuItemRequested$.pipe(
        switchMap((seitenId: string) => interval(100).pipe(
            filter(_ => !this._uiControlsRequestPending),
            switchMap(_ => menuItemRequestedObs$(seitenId)),
            takeWhile(() => this._uiControlsRequestPending)))).subscribe();

    const menuItemRequestedObs$ = (menuItemRequested: string) => this._eventService.uiControls$.pipe(
        withLatestFrom(this._eventService.menuState$, this._eventService.menuItemSelected$, this._eventService.highestClickedMenuItem$),
        take(1),
        filter(([_, __, menuItemSelected, ___]) => !!menuItemRequested && !!menuItemSelected &&
            menuItemRequested !== menuItemSelected),
        tap(([uiControl, menuState, menuItemSelected, highestClickedMenuItemIndex]) => {
          const menuSeiten = getAllMenuStateSeiten(menuState);
          let currentMenuItemSelectedIndex = menuSeiten.findIndex(seite => menuItemSelected === seite.Identifikator);
          let requestedMenuItemIndex = menuSeiten.findIndex(seite => menuItemRequested === seite.Identifikator);
          if (currentMenuItemSelectedIndex < 0) {
            currentMenuItemSelectedIndex = menuState.MenuGruppen.findIndex(gruppe => menuItemSelected === gruppe.Identifikator);
            requestedMenuItemIndex = menuState.MenuGruppen.findIndex(gruppe => menuItemRequested === gruppe.Identifikator);
          }
          const isStepForward = (requestedMenuItemIndex - currentMenuItemSelectedIndex) > 0;
          if (isStepForward) {
            this._eventService.validateSeiteSelected$.next(menuItemSelected);
            const seiteSelected = menuSeiten.filter(seite => menuItemSelected === seite.Identifikator)[0];
            const seiteRequested = menuSeiten.filter(seite => menuItemRequested === seite.Identifikator)[0];
            if (seiteSelected?.valid && seiteRequested?.enabled && this._hasNoAllgemeineMeldung(uiControl) ||
                this._shellConfigService.getParameter().uiWorkflowValidierung ===
                UIWorkflowValidierung.WORKFLOW_VALIDIERUNG_ALLES_GLEICHZEITIG) {
              this._eventService.menuItemSelected$.next(menuItemRequested);
            } else {
              this._eventService.menuItemSelected$.next(menuItemSelected);
              if (votumBlocksWorkflow(uiControl) && !!this._shellConfigService.getParameter().uiWorkflowStopBeiVotum) {
                this.snackBar.open(this._translationService.translate('ERRORS.SNACK_BAR_VOTUM'), '', this._getSnackBarConfig());
              } else {
                this.snackBar.open(this._translationService.translate('ERRORS.SNACK_BAR'), '', this._getSnackBarConfig());
              }
            }
          } else {
            this._eventService.menuItemSelected$.next(menuItemRequested);
          }
          if (requestedMenuItemIndex > highestClickedMenuItemIndex) {
            this._eventService.highestClickedMenuItem$.next(requestedMenuItemIndex);
          }
        })
    );

    const weiterZurueckClicked$ = (index: number) => this._eventService.menuState$.pipe(
        withLatestFrom(this._eventService.menuItemSelected$),
        take(1),
        tap(([menuState, menuItemSelected]) => {
          const menuSeiten = getAllMenuStateSeiten(menuState);
          let currentMenuItemSelectedIndex = menuSeiten.findIndex(seite => menuItemSelected === seite.Identifikator);
          let identifierForNextMenuItem = menuSeiten[currentMenuItemSelectedIndex + index]?.Identifikator;
          if (currentMenuItemSelectedIndex < 0) {
            currentMenuItemSelectedIndex = menuState.MenuGruppen.findIndex(gruppe => menuItemSelected === gruppe.Identifikator);
            identifierForNextMenuItem = menuState.MenuGruppen[currentMenuItemSelectedIndex + index]?.Identifikator;
          }
          this._eventService.menuItemRequested$.next(identifierForNextMenuItem);
        })
    );

    const navigtionButtonClicked$ = (seitenId: string) => this._eventService.menuState$.pipe(
        take(1),
        tap(menuState => {
          const menuSeiten = getAllMenuStateSeiten(menuState);
          const indexForNextMenuItem = menuSeiten.findIndex(seite => seitenId === seite.SeitenId);
          const identifierForNextMenuItem = menuSeiten[indexForNextMenuItem]?.Identifikator;
          this._eventService.menuItemRequested$.next(identifierForNextMenuItem);
        })
    );

    this._subscription.add(this._eventService.weiterButtonClicked$.pipe(switchMap(_ => weiterZurueckClicked$(1))).subscribe());
    this._subscription.add(this._eventService.zurueckButtonClicked$.pipe(switchMap(_ => weiterZurueckClicked$(-1))).subscribe());
    this._subscription.add(this._eventService.navigationButtonClicked$.pipe(switchMap(id => navigtionButtonClicked$(id))).subscribe());
  }

  public ngOnDestroy() {
    if (!!this._uiControlsRequestPendingSubscription$) {
      this._uiControlsRequestPendingSubscription$.unsubscribe();
    }
    if (!!this._subscription) {
      this._subscription.unsubscribe();
    }
  }

  private _hasNoAllgemeineMeldung(uiControl) {
    if (!uiControl.uiVorgang.UIMeldung || !uiControl.uiVorgang.UIMeldung.length) {
      return true;
    }
    return !uiControl.uiVorgang.UIMeldung.filter(meldung => meldung.Meldungskategorie === 'Fehler').length;
  }

  private _getSnackBarConfig(): MatSnackBarConfig {
    const config = new MatSnackBarConfig();
    config.verticalPosition = 'bottom';
    config.horizontalPosition = 'center';
    config.duration = 4000;
    return config;
  }
}
